/**
 * heartUpgrades.ts
 * Pure helpers that turn persisted Heart Fragment pickups into Nova's max HP.
 *
 * Heart Fragments are permanent progression: each one is worth
 * {@link HEART_FRAGMENT_HP_VALUE} (0.5) HP and two of them grant one whole
 * heart. The fragment ids are persisted per level in
 * `SaveData.levels[key].collectedHeartFragmentIds`, so the upgrade is
 * reconstructed from the save on every level load rather than carried in
 * memory.
 *
 * DECISION: Grant whole hearts only — `NOVA_MAX_HP + floor(fragments * 0.5)`.
 * A fractional max HP cannot be rendered: `heartsString()` builds the HUD with
 * `'♥'.repeat(filled)`, and `String.prototype.repeat` truncates its argument,
 * so a 3.5 max HP draws identically to 3. Flooring keeps the model and the
 * display in agreement and keeps every HP value an integer.
 *
 * @module heartUpgrades
 */

import { HEART_FRAGMENT_HP_VALUE, NOVA_MAX_HP } from '../../constants/gameConstants';
import type { SaveData } from '../types/save';

/**
 * Total Heart Fragments collected across every level in the save.
 *
 * Every level entry counts, including secret (`X-s`/`X-S`) and boss (`X-B`)
 * stages — unlike shard counting, there is no per-world denominator to keep
 * the total under, and fragments are placed in secret stages too.
 *
 * @param save - SaveData to read, or null for a missing/corrupt save.
 * @returns Total collected fragments (0 when the save is null).
 */
export function countCollectedHeartFragments(save: SaveData | null): number {
  if (save == null) return 0;
  let total = 0;
  for (const progress of Object.values(save.levels)) {
    total += progress.collectedHeartFragmentIds.length;
  }
  return total;
}

/**
 * Nova's max HP for a given number of collected Heart Fragments.
 *
 * @param fragments - Total fragments collected. Non-finite, negative and
 *                    fractional inputs are clamped to a sane whole count so a
 *                    hand-edited save cannot produce a NaN max HP.
 * @returns `NOVA_MAX_HP` plus one HP per completed pair of fragments.
 */
export function maxHealthForFragments(fragments: number): number {
  if (!Number.isFinite(fragments)) return NOVA_MAX_HP;
  const whole = Math.max(0, Math.floor(fragments));
  return NOVA_MAX_HP + Math.floor(whole * HEART_FRAGMENT_HP_VALUE);
}
